import {
    useEffect,
    useState,
} from 'react';
import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Button,
    Code,
    Group,
    Modal,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';

import {DnsZone} from '../types';
import {DelegationBadge} from './DelegationBadge';
import {zoneDeletionBlock} from '../hooks/useZoneDeletionGuard';
import {classifyWriteError} from '../services/writeErrors';
import {
    isWriteErrorRetryable,
    writeErrorCopy,
} from '../writeErrorMessages';

// FNP-1191 CRUD half — THE CONFIRMATION, AND THE TWO REFUSALS.
//
// ⚠️ A CONTROLLED Mantine `Modal`, NOT `modals.openConfirmModal`.
//
// The DELETE pattern doc reaches for `modals.openConfirmModal`, and that is the
// right call in the apps it was written from. It is NOT available here:
// @mantine/modals is a dependency but NO `ModalsProvider` is mounted in
// src/App.tsx, so `modals.openConfirmModal` would silently do nothing — the
// worst possible failure for a destructive-action guard, because the delete
// button would appear inert rather than broken. Mounting a provider in App.tsx
// is an application-shell change and this ticket is a module ticket.
//
// The doc's SUBSTANCE is kept in full: confirm before deleting, name what is
// being deleted, red confirm action, clear selection after. Only the mechanism
// differs, and it differs because the doc's mechanism does not work in this app.
//
// ⚠️ THIS MODAL NAMES THE ZONE AND SHOWS ITS DELEGATION. A confirmation that
// says "delete 1 item?" is a rubber stamp — the operator already knows they
// clicked delete. What they may NOT know is that this particular zone is live,
// which is the fact that should stop them.

export const DeleteZoneModal = (
    {
        zone,
        opened,
        onClose,
        onConfirm,
        isDeleting = false,
        error,
    }: {
        zone?: DnsZone,
        opened: boolean,
        onClose: () => void,
        onConfirm: () => void,
        isDeleting?: boolean,
        // The rejected mutation, unclassified. Classified here so the modal can
        // render the refusal INLINE — a toast is gone before an operator has
        // finished reading it, and ZONE_NOT_EMPTY is a message they must act on.
        error?: unknown,
    },
) => {

    // The typed-name confirmation for a live zone. Reset whenever the modal
    // opens or the zone changes, or a previous confirmation would carry over
    // and pre-arm the button for a DIFFERENT zone.
    const [typedName, setTypedName] = useState('');

    useEffect(() => {
        setTypedName('');
    }, [opened, zone?.id]);

    if (!zone) {
        return null;
    }

    const block = zoneDeletionBlock(zone);
    const writeError = error ? classifyWriteError(error) : undefined;

    // ⚠️ A HARD BLOCK ARMS ONLY ON AN EXACT NAME MATCH.
    //
    // Deleting a live zone is not something to reach by clicking through a
    // dialog with the same gesture that opened it. Typing the zone name is the
    // friction — it forces the operator to READ which zone this is, which is
    // the failure this modal exists to prevent (deleting the wrong zone from a
    // list of similar names).
    const hardBlocked = block?.overridable === false;
    const nameConfirmed = typedName.trim() === zone.name;
    const confirmDisabled = isDeleting || (hardBlocked && !nameConfirmed);

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={I18n.get('Delete zone')}
            size={'lg'}
            centered={true}
        >
            <Stack gap={'md'}>

                {/* NAMES WHAT IS BEING DELETED — the pattern doc's requirement,
                    and the reason this is not a generic "1 item" dialog. */}
                <Stack gap={4}>
                    <Text size={'sm'}>{I18n.get('You are about to delete this zone:')}</Text>
                    <Group gap={'xs'}>
                        <Code>{zone.name}</Code>
                        <DelegationBadge zone={zone}/>
                    </Group>
                    <Text size={'xs'} c={'dimmed'}>
                        {I18n.get('Connection')}: <Code>{zone.connectionId}</Code>
                    </Text>
                </Stack>

                {/* ─── GUARD 1: DELEGATION (checked before the attempt) ─── */}
                {block && (
                    <Alert
                        color={block.overridable ? 'yellow' : 'red'}
                        variant={'light'}
                        title={block.title}
                    >
                        <Stack gap={'xs'}>
                            <Text size={'sm'}>{block.explanation}</Text>
                            {hardBlocked && (
                                <TextInput
                                    label={I18n.get('Type the zone name to confirm')}
                                    description={I18n.get('This zone is answering live DNS. Typing its name confirms you mean this one.')}
                                    placeholder={zone.name}
                                    value={typedName}
                                    onChange={event => setTypedName(event.currentTarget.value)}
                                />
                            )}
                        </Stack>
                    </Alert>
                )}

                {/* ─── GUARD 2: THE PROVIDER'S REFUSAL (after the attempt) ───
                    ZONE_NOT_EMPTY above all. Rendered here rather than only as
                    a toast, because it names a concrete next step and the
                    operator is still looking at this dialog. */}
                {writeError && (
                    <Alert
                        color={'red'}
                        variant={'light'}
                        title={writeErrorCopy(writeError.kind).title}
                    >
                        <Stack gap={'xs'}>
                            <Text size={'sm'}>
                                {writeErrorCopy(writeError.kind).action}
                            </Text>
                            {/* The provider's own words, VERBATIM. */}
                            {writeError.providerMessage && (
                                <Text size={'xs'} c={'dimmed'}>
                                    {writeError.providerMessage}
                                </Text>
                            )}
                            <Code>{writeError.kind}</Code>
                        </Stack>
                    </Alert>
                )}

                <Text size={'sm'}>
                    {I18n.get('This cannot be undone. The zone and its records are removed at the provider, and Flexi does not keep a copy to restore from.')}
                </Text>

                <Group justify={'flex-end'}>
                    <Button variant={'default'} onClick={onClose} disabled={isDeleting}>
                        {I18n.get('Cancel')}
                    </Button>
                    <Button
                        color={'red'}
                        onClick={onConfirm}
                        loading={isDeleting}
                        disabled={confirmDisabled}
                    >
                        {/* Retryable failures say so; ZONE_NOT_EMPTY does NOT
                            offer a retry, because clicking again cannot succeed
                            until the records are gone. */}
                        {writeError && isWriteErrorRetryable(writeError.kind)
                            ? I18n.get('Try again')
                            : I18n.get('Delete zone')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
